"use client";

import * as React from "react";
import {
  UserProfileError,
  UserProfileLoading,
  UserProfile as UserProfilePresentational,
} from "./lib/user-profile.js";
import type {
  UserProfileErrorProps,
  UserProfileLoadingProps,
} from "./lib/user-profile.js";
import { useMe } from "./api/endpoint.js";
import { ApiProvider, AuthToken, useApiReady } from "./api/api-provider.js";
import { useWorkOsApiUrl } from "./lib/widgets-context.js";
import { ErrorBoundary } from "./lib/error-boundary.js";
import type { WidgetRootDomProps } from "./lib/utils.js";

interface UserProfileProps extends WidgetRootDomProps {
  authToken: AuthToken;
}

const UserProfile: React.FC<UserProfileProps> = ({
  authToken,
  ...domProps
}) => {
  const baseUrl = useWorkOsApiUrl();

  return (
    <ErrorBoundary
      fallbackRender={({ error }) => (
        <UserProfileError {...domProps} error={error} />
      )}
    >
      <ApiProvider
        widgetType="user-profile"
        authToken={authToken}
        baseUrl={baseUrl}
      >
        <UserProfileImpl {...domProps} />
      </ApiProvider>
    </ErrorBoundary>
  );
};

const UserProfileImpl = (props: WidgetRootDomProps) => {
  const isApiReady = useApiReady();
  const meQuery = useMe();

  if (!isApiReady || meQuery.isLoading) {
    return <UserProfileLoading {...props} />;
  }

  if (meQuery.isError) {
    return <UserProfileError error={meQuery.error} {...props} />;
  }

  const user = meQuery.data!;

  return (
    <ErrorBoundary
      fallbackRender={({ error }) => (
        <UserProfileError error={error} {...props} />
      )}
    >
      <UserProfilePresentational userData={user} {...props} />
    </ErrorBoundary>
  );
};

export type {
  UserProfileProps,
  UserProfileLoadingProps,
  UserProfileErrorProps,
};
export { UserProfile, UserProfileLoading, UserProfileError };
